/**
 * TagsManager Component
 *
 * Admin component to manage CRM tags stored in Supabase.
 *
 * Architecture:
 * - Tags live in the Supabase crm_tags table as ONE namespace shared by
 *   contacts and deals (crm_contacts.tags is the store; crm_deals.tags mirrors
 *   it), so there is a single list rather than separate contact/deal lists.
 * - visibility column: 'global' (everyone sees) or 'personal' (owner + admins)
 * - owner_id column: Firebase UID of personal tag owner (null for global)
 * - Real-time updates via CrmSettingsContext subscription
 *
 * Admin Features:
 * - View all tags (global + all users' personal tags + discovered)
 * - Promote personal tags to global
 * - Create, edit and soft-delete tags
 * - Merge one tag into another (rewrites every contact and deal that uses it)
 */

import React, { useState, useMemo } from 'react';
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  FormHelperText,
  Input,
  InputGroup,
  InputLeftElement,
  Textarea,
  Select as ChakraSelect,
  useToast,
  useColorModeValue,
  Icon,
  Center,
  Spinner,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiTag,
  FiRefreshCw,
  FiGlobe,
  FiUser,
  FiArrowUp,
  FiGitMerge,
  FiSearch,
} from 'react-icons/fi';
import { supabase, isSupabaseConfigured } from '../../../utils/supabase';
import { useCrmSettings } from '../../../contexts/CrmSettingsContext';
import { useAuth } from '../../../contexts/AuthContext';
import { useTagRemovalConfirm } from '../ConfirmTagRemovalDialog';

const AVAILABLE_COLORS = [
  { value: 'gray', label: 'Gray' },
  { value: 'red', label: 'Red' },
  { value: 'orange', label: 'Orange' },
  { value: 'yellow', label: 'Yellow' },
  { value: 'green', label: 'Green' },
  { value: 'teal', label: 'Teal' },
  { value: 'blue', label: 'Blue' },
  { value: 'cyan', label: 'Cyan' },
  { value: 'purple', label: 'Purple' },
  { value: 'pink', label: 'Pink' },
];

/**
 * TagsManager - Admin component for managing CRM tags in Supabase
 */
export default function TagsManager() {
  const toast = useToast();
  const { getAllTags, tagsLoading, refreshTags } = useCrmSettings();
  const { currentUser } = useAuth();
  const { confirmTagRemoval, dialog: removalDialog } = useTagRemovalConfirm();

  const [selectedTag, setSelectedTag] = useState(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isMergeOpen, onOpen: onMergeOpen, onClose: onMergeClose } = useDisclosure();

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const tableHeaderBG = useColorModeValue('gray.50', 'gray.800');
  const hoverBG = useColorModeValue('gray.50', 'gray.700');

  const allTags = getAllTags();

  const visibleTags = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return allTags;
    return allTags.filter(
      tag =>
        tag.value.toLowerCase().includes(term) ||
        (tag.label || '').toLowerCase().includes(term)
    );
  }, [allTags, search]);

  // Check if Supabase is configured
  if (!isSupabaseConfigured()) {
    return (
      <Alert status="warning">
        <AlertIcon />
        Supabase is not configured. Tags cannot be managed.
      </Alert>
    );
  }

  const handleAddTag = () => {
    setSelectedTag(null);
    onOpen();
  };

  const handleEditTag = (tag) => {
    setSelectedTag(tag);
    onOpen();
  };

  const handleMergeTag = (tag) => {
    setSelectedTag(tag);
    onMergeOpen();
  };

  /** Find the single registry row backing a tag in the admin list. */
  const findTagRow = async (tag) => {
    let query = supabase.from('crm_tags').select('id').eq('value', tag.value);
    query = tag.ownerId ? query.eq('owner_id', tag.ownerId) : query.is('owner_id', null);

    const { data, error } = await query.single();
    if (error) throw error;
    return data;
  };

  const handleDeleteTag = async (tag) => {
    // Deleting is a soft delete (is_active = false): it only takes the tag out
    // of the pickers. Records keep the value and surface it as a "discovered"
    // tag, so the wording has to say so rather than imply the data is cleaned.
    const confirmed = await confirmTagRemoval({
      title: 'Delete this tag?',
      tags: [tag.label],
      description: 'This removes the tag from the tag list so nobody can pick it again.',
      note: 'Contacts and deals already tagged with it keep the tag and will show it as a "discovered" tag. Use Merge instead if you want to move those records onto another tag.',
      confirmLabel: 'Delete tag',
    });

    if (!confirmed) return;

    setSaving(true);
    try {
      const existingTag = await findTagRow(tag);

      const { error: deleteError } = await supabase
        .from('crm_tags')
        .update({ is_active: false })
        .eq('id', existingTag.id);

      if (deleteError) throw deleteError;

      toast({
        title: 'Tag deleted',
        status: 'success',
        duration: 2000,
      });

      refreshTags();
    } catch (error) {
      console.error('Error deleting tag:', error);
      toast({
        title: 'Error deleting tag',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setSaving(false);
    }
  };

  /**
   * Promote a personal tag to global visibility
   * This makes the tag visible to all users
   */
  const handlePromoteTag = async (tag) => {
    if (!tag.ownerId) {
      toast({
        title: 'Already global',
        description: 'This tag is already visible to everyone',
        status: 'info',
        duration: 2000,
      });
      return;
    }

    if (!window.confirm(`Promote "${tag.label}" to a global tag? It will be visible to all users.`)) {
      return;
    }

    setSaving(true);
    try {
      const existingTag = await findTagRow(tag);

      const { error: updateError } = await supabase
        .from('crm_tags')
        .update({
          visibility: 'global',
          owner_id: null, // Clear owner since it's now global
          updated_at: new Date().toISOString(),
        })
        .eq('id', existingTag.id);

      if (updateError) throw updateError;

      toast({
        title: 'Tag promoted',
        description: `"${tag.label}" is now visible to all users`,
        status: 'success',
        duration: 3000,
      });

      refreshTags();
    } catch (error) {
      console.error('Error promoting tag:', error);
      toast({
        title: 'Error promoting tag',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTag = async (tagData) => {
    setSaving(true);
    try {
      if (selectedTag) {
        const existingTag = await findTagRow(selectedTag);

        const { error: updateError } = await supabase
          .from('crm_tags')
          .update({
            value: tagData.value,
            label: tagData.label,
            color: tagData.color,
            description: tagData.description,
            updated_at: new Date().toISOString(),
          })
          .eq('id', existingTag.id);

        if (updateError) throw updateError;

        toast({
          title: 'Tag updated',
          status: 'success',
          duration: 2000,
        });
      } else {
        // Reactivate a soft-deleted row instead of colliding with it: is_active
        // is not part of the unique key, so a plain insert would raise 23505.
        const { data: existing } = await supabase
          .from('crm_tags')
          .select('id, is_active')
          .eq('value', tagData.value)
          .is('owner_id', null)
          .maybeSingle();

        if (existing) {
          if (existing.is_active) {
            throw new Error('A tag with this value already exists');
          }

          const { error: reviveError } = await supabase
            .from('crm_tags')
            .update({
              label: tagData.label,
              color: tagData.color,
              description: tagData.description,
              is_active: true,
              visibility: 'global',
              updated_at: new Date().toISOString(),
            })
            .eq('id', existing.id);

          if (reviveError) throw reviveError;
        } else {
          const { error: insertError } = await supabase
            .from('crm_tags')
            .insert({
              value: tagData.value,
              label: tagData.label,
              color: tagData.color,
              description: tagData.description,
              entity_type: 'contact',
              is_active: true,
            });

          if (insertError) {
            if (insertError.code === '23505') {
              throw new Error('A tag with this value already exists');
            }
            throw insertError;
          }
        }

        toast({
          title: 'Tag created',
          status: 'success',
          duration: 2000,
        });
      }

      refreshTags();
      onClose();
    } catch (error) {
      console.error('Error saving tag:', error);
      toast({
        title: 'Error saving tag',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmMerge = async (targetValue) => {
    // Merging rewrites every record carrying the tag and then deletes it, with
    // no undo, so it gets the same prompt as a removal.
    const confirmed = await confirmTagRemoval({
      title: 'Merge and delete this tag?',
      tags: [selectedTag.value],
      description: `Every contact and deal tagged "${selectedTag.value}" will be re-tagged with "${targetValue}", and "${selectedTag.value}" will be deleted.`,
      note: 'This cannot be undone. Campaign triggers, smart lists and saved filters that reference the old value will need updating by hand.',
      confirmLabel: 'Merge tags',
      confirmColorScheme: 'purple',
    });

    if (!confirmed) return;

    setSaving(true);
    try {
      const { data, error } = await supabase.rpc('crm_tag_merge', {
        p_from_value: selectedTag.value,
        p_to_value: targetValue,
        p_actor_id: currentUser?.uid || null,
      });

      if (error) throw error;

      toast({
        title: 'Tags merged',
        description: `"${selectedTag.value}" → "${targetValue}" across ${data?.contactsUpdated || 0} contacts and ${data?.dealsUpdated || 0} deals`,
        status: 'success',
        duration: 6000,
      });

      refreshTags();
      onMergeClose();
    } catch (error) {
      console.error('Error merging tags:', error);
      toast({
        title: 'Error merging tags',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setSaving(false);
    }
  };

  if (tagsLoading) {
    return (
      <Center py={8}>
        <Spinner size="lg" />
      </Center>
    );
  }

  return (
    <Box>
      <HStack justify="space-between" mb={4}>
        <Heading size="md">Tag Management</Heading>
        <HStack>
          <Button
            size="sm"
            leftIcon={<FiRefreshCw />}
            variant="ghost"
            onClick={refreshTags}
          >
            Refresh
          </Button>
          <Button
            size="sm"
            leftIcon={<FiPlus />}
            colorScheme="blue"
            onClick={handleAddTag}
          >
            Add Tag
          </Button>
        </HStack>
      </HStack>

      <Text color="gray.600" mb={4}>
        One tag list is shared by contacts and deals — tagging a deal tags its contact and vice versa.
      </Text>

      <HStack mb={4} justify="space-between">
        <InputGroup size="sm" maxW="320px">
          <InputLeftElement pointerEvents="none">
            <Icon as={FiSearch} color="gray.400" />
          </InputLeftElement>
          <Input
            placeholder="Search tags..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </InputGroup>
        <Badge fontSize="sm">
          {visibleTags.length}
          {visibleTags.length !== allTags.length ? ` of ${allTags.length}` : ''} tags
        </Badge>
      </HStack>

      <Box
        bg={bgColor}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="md"
        overflow="hidden"
      >
        <Box overflowX="auto">
          <Table variant="simple" size="sm">
            <Thead bg={tableHeaderBG}>
              <Tr>
                <Th>Value</Th>
                <Th>Label</Th>
                <Th>Color</Th>
                <Th>Visibility</Th>
                <Th width="150px">Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {visibleTags.map((tag, index) => (
                <Tr key={`${tag.value}-${tag.ownerId || 'global'}-${index}`} _hover={{ bg: hoverBG }}>
                  <Td>
                    <Text fontFamily="mono" fontSize="sm">{tag.value}</Text>
                  </Td>
                  <Td fontWeight="medium">{tag.label}</Td>
                  <Td>
                    <Badge colorScheme={tag.color || 'gray'}>{tag.color || 'gray'}</Badge>
                  </Td>
                  <Td>
                    {tag.isGlobal ? (
                      <Badge colorScheme="green" variant="subtle">
                        <HStack spacing={1}>
                          <Icon as={FiGlobe} boxSize={3} />
                          <Text>Global</Text>
                        </HStack>
                      </Badge>
                    ) : tag.isPersonal ? (
                      <Badge colorScheme="blue" variant="subtle">
                        <HStack spacing={1}>
                          <Icon as={FiUser} boxSize={3} />
                          <Text>Personal</Text>
                        </HStack>
                      </Badge>
                    ) : tag.isDiscovered ? (
                      <Badge colorScheme="orange" variant="subtle">
                        <HStack spacing={1}>
                          <Icon as={FiTag} boxSize={3} />
                          <Text>Discovered</Text>
                        </HStack>
                      </Badge>
                    ) : (
                      <Badge colorScheme="gray">Unknown</Badge>
                    )}
                  </Td>
                  <Td>
                    <HStack spacing={1}>
                      {(tag.isPersonal || tag.isDiscovered) && (
                        <IconButton
                          icon={<FiArrowUp />}
                          size="xs"
                          variant="ghost"
                          colorScheme="green"
                          aria-label="Promote to global"
                          title="Promote to global tag"
                          onClick={() => handlePromoteTag(tag)}
                          isLoading={saving}
                        />
                      )}
                      <IconButton
                        icon={<FiGitMerge />}
                        size="xs"
                        variant="ghost"
                        colorScheme="purple"
                        aria-label="Merge into another tag"
                        title="Merge into another tag"
                        onClick={() => handleMergeTag(tag)}
                      />
                      <IconButton
                        icon={<FiEdit2 />}
                        size="xs"
                        variant="ghost"
                        aria-label="Edit tag"
                        onClick={() => handleEditTag(tag)}
                      />
                      <IconButton
                        icon={<FiTrash2 />}
                        size="xs"
                        variant="ghost"
                        colorScheme="red"
                        aria-label="Delete tag"
                        onClick={() => handleDeleteTag(tag)}
                        isLoading={saving}
                      />
                    </HStack>
                  </Td>
                </Tr>
              ))}
              {visibleTags.length === 0 && (
                <Tr>
                  <Td colSpan={5}>
                    <Text color="gray.500" textAlign="center" py={4}>
                      {allTags.length === 0
                        ? 'No tags defined. Click "Add Tag" to create one.'
                        : 'No tags match your search.'}
                    </Text>
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </Box>
      </Box>

      {/* Tag Editor Modal */}
      <TagEditorModal
        isOpen={isOpen}
        onClose={onClose}
        tag={selectedTag}
        onSave={handleSaveTag}
        saving={saving}
      />

      {/* Merge Modal */}
      <TagMergeModal
        isOpen={isMergeOpen}
        onClose={onMergeClose}
        tag={selectedTag}
        allTags={allTags}
        onMerge={handleConfirmMerge}
        saving={saving}
      />

      {removalDialog}
    </Box>
  );
}

/**
 * Tag Editor Modal
 */
function TagEditorModal({ isOpen, onClose, tag, onSave, saving }) {
  const [value, setValue] = useState('');
  const [label, setLabel] = useState('');
  const [color, setColor] = useState('gray');
  const [description, setDescription] = useState('');

  // Reset form when modal opens
  React.useEffect(() => {
    if (isOpen) {
      if (tag) {
        setValue(tag.value || '');
        setLabel(tag.label || '');
        setColor(tag.color || 'gray');
        setDescription(tag.description || '');
      } else {
        setValue('');
        setLabel('');
        setColor('gray');
        setDescription('');
      }
    }
  }, [isOpen, tag]);

  // Auto-generate value from label
  const handleLabelChange = (e) => {
    const newLabel = e.target.value;
    setLabel(newLabel);
    
    // Only auto-generate value if this is a new tag
    if (!tag) {
      const autoValue = newLabel
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .trim();
      setValue(autoValue);
    }
  };

  const handleSubmit = () => {
    if (!value.trim() || !label.trim()) {
      return;
    }

    onSave({
      value: value.trim(),
      label: label.trim(),
      color,
      description: description.trim(),
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{tag ? 'Edit' : 'Add'} Tag</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4}>
            <FormControl isRequired>
              <FormLabel>Label</FormLabel>
              <Input
                value={label}
                onChange={handleLabelChange}
                placeholder="e.g., Past Client"
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Value (slug)</FormLabel>
              <Input
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="e.g., past-client"
                fontFamily="mono"
                isDisabled={!!tag} // Don't allow changing value for existing tags
              />
              <FormHelperText fontSize="xs">
                {tag
                  ? 'Use Merge to change the stored value of an existing tag.'
                  : 'Auto-generated from label. Used in database.'}
              </FormHelperText>
            </FormControl>

            <FormControl>
              <FormLabel>Color</FormLabel>
              <ChakraSelect
                value={color}
                onChange={(e) => setColor(e.target.value)}
              >
                {AVAILABLE_COLORS.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </ChakraSelect>
              <HStack mt={2}>
                <Text fontSize="sm">Preview:</Text>
                <Badge colorScheme={color}>{label || 'Tag'}</Badge>
              </HStack>
            </FormControl>

            <FormControl>
              <FormLabel>Description (optional)</FormLabel>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Notes about when to use this tag..."
                rows={2}
              />
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            isLoading={saving}
            isDisabled={!value.trim() || !label.trim()}
          >
            {tag ? 'Save Changes' : 'Create Tag'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

/**
 * Merge one tag into another.
 *
 * Rewrites the stored value on every contact and deal that carries it, then
 * retires the source registry row. The rewrite is deliberately silent so a
 * cleanup does not enroll thousands of records into tag-triggered campaigns.
 */
function TagMergeModal({ isOpen, onClose, tag, allTags, onMerge, saving }) {
  const [target, setTarget] = useState('');

  React.useEffect(() => {
    if (isOpen) setTarget('');
  }, [isOpen]);

  const options = useMemo(
    () => allTags.filter(t => t.value !== tag?.value).map(t => t.value).sort(),
    [allTags, tag]
  );

  if (!tag) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Merge Tag</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Alert status="warning" fontSize="sm">
              <AlertIcon />
              <Text>
                Every contact and deal tagged <strong>{tag.value}</strong> will be
                re-tagged with the target, and <strong>{tag.value}</strong> will be
                deleted. This cannot be undone.
              </Text>
            </Alert>

            <FormControl isRequired>
              <FormLabel>Merge into</FormLabel>
              <ChakraSelect
                placeholder="Select the tag to keep..."
                value={target}
                onChange={(e) => setTarget(e.target.value)}
              >
                {options.map(value => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </ChakraSelect>
              <FormHelperText fontSize="xs">
                Campaign triggers, smart lists and saved filters that reference{' '}
                <strong>{tag.value}</strong> will need updating to the target value.
              </FormHelperText>
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="purple"
            onClick={() => onMerge(target)}
            isLoading={saving}
            isDisabled={!target}
          >
            Merge
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
